import type { CSSProperties, ReactElement, ReactNode } from "react";
import { space, fluidSpace, type SpaceKey, type FluidSpaceKey } from "@tokens/index";

/**
 * Stack — the core layout primitive.
 *
 * Atlas 02 §4 ('ma' — active negative space): a stack's gap is not absence,
 * it is *interval*. Two distinct spacing modes live here:
 *
 *  - `gap` (numeric SpaceKey)        — fixed metric, for component-internal
 *                                       rhythm. Use when you want every
 *                                       breakpoint to share the same exact
 *                                       interval.
 *  - `gap` ("xs"|"sm"|...|"3xl")     — fluid (`clamp()`), for page-level
 *                                       rhythm. Use when the interval should
 *                                       breathe with the viewport.
 *
 * `wrap`, `inline`, and `as` are added so the same primitive can be a row
 * of badges, a wrapping chip group, or the page's vertical spine — without
 * ad-hoc style props at the callsite.
 */

export type StackDirection = "row" | "column" | "row-reverse" | "column-reverse";
export type StackAlign = "start" | "center" | "end" | "stretch" | "baseline";
export type StackJustify = "start" | "center" | "end" | "between" | "around" | "evenly";
export type StackWrap = "nowrap" | "wrap" | "wrap-reverse";
export type StackGap = SpaceKey | FluidSpaceKey;

export interface StackProps {
    direction?: StackDirection;
    /**
     * Numeric key (0..10) for fixed metric spacing, or fluid key
     * ("xs".."3xl") for clamp()-based viewport-fluid spacing.
     */
    gap?: StackGap;
    align?: StackAlign;
    justify?: StackJustify;
    wrap?: StackWrap;
    /**
     * Render as inline-flex instead of flex. Useful for chip rows and
     * inline-meta groupings that should sit alongside text.
     */
    inline?: boolean;
    /**
     * HTML element to render. Defaults to `div`. Use `nav`, `header`,
     * `section`, `ul`/`ol` etc. for semantic correctness.
     */
    as?:
        | "div"
        | "section"
        | "header"
        | "footer"
        | "nav"
        | "main"
        | "aside"
        | "ul"
        | "ol"
        | "article";
    /**
     * Children grow to fill available space along the main axis when true.
     * Equivalent to `flex: 1` on each immediate child via `flexGrow: 1` —
     * implemented at the parent via `> *` selectors is impossible inline,
     * so this currently sets `alignItems` and leaves growth to the children.
     * Provided as a documentation hook for future enhancement.
     */
    fill?: boolean;
    children: ReactNode;
}

const alignMap: Record<StackAlign, CSSProperties["alignItems"]> = {
    start: "flex-start",
    center: "center",
    end: "flex-end",
    stretch: "stretch",
    baseline: "baseline",
};

const justifyMap: Record<StackJustify, CSSProperties["justifyContent"]> = {
    start: "flex-start",
    center: "center",
    end: "flex-end",
    between: "space-between",
    around: "space-around",
    evenly: "space-evenly",
};

function resolveGap(gap: StackGap): string {
    // Fluid keys are strings ("xs", "sm", ...). Numeric keys come in as
    // either number (TS literal) or numeric-string (object key).
    if (typeof gap === "number") {
        return space[gap];
    }
    if (gap in fluidSpace) {
        return fluidSpace[gap as FluidSpaceKey];
    }
    return space[gap as unknown as SpaceKey];
}

export function Stack(props: StackProps): ReactElement {
    const direction = props.direction ?? "column";
    const gap = props.gap ?? 3;
    const align = props.align ?? "stretch";
    const justify = props.justify ?? "start";
    const wrap = props.wrap ?? "nowrap";
    const inline = props.inline ?? false;
    const Tag = (props.as ?? "div") as React.ElementType;

    const style: CSSProperties = {
        display: inline ? "inline-flex" : "flex",
        flexDirection: direction,
        flexWrap: wrap,
        gap: resolveGap(gap),
        alignItems: alignMap[align],
        justifyContent: justifyMap[justify],
        // `min-width: 0` is the canonical fix for flex children whose
        // overflowed text would otherwise blow out their parent's width.
        // Atlas 03 §3, "Every Layout" — defensive layout primitives.
        minWidth: 0,
    };

    return (
        <Tag data-direction={direction} data-gap={String(gap)} style={style}>
            {props.children}
        </Tag>
    );
}
